/**
 * useDripReferral — one hook to power every drip email's in-app landing.
 * =========================================================================
 *
 * Every drip email deep-links back to the app with `?ref=drip<code>`:
 *   Day 7  → /behavior?ref=drip7
 *   Day 14 → /behavior?ref=drip14
 *   Day 21 → /appointment?ref=drip21
 *   Day 28 → /burnout?ref=drip28
 *   Weekly → /behavior?ref=drip_weekly · /dashboard?ref=drip_weekly
 *
 * This hook does four things on mount:
 *   1. Reads ?ref from the URL and maps it to a backend `kind`.
 *   2. Fires POST /api/me/drip-click so we can attribute the click.
 *   3. Fires GA4 `drip_click{kind}` for the founder analytics.
 *   4. Strips ?ref from the URL so a browser refresh doesn't double-count.
 *
 * Return shape:
 *   { kind, lovedOneName, dismissed, dismiss }
 * `kind` is `null` when the page wasn't reached via a drip — callers use
 * that to decide whether to render the banner.
 */
import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import { track } from "@/lib/analytics";

// ref-code → backend kind. Keep in sync with routes/drip_click.py
// _VALID_KINDS whitelist.
const REF_TO_KIND = {
    drip7: "day7",
    drip14: "day14",
    drip21: "day21",
    drip28: "day28",
    drip_weekly: "post28_weekly",
    drip_welcome: "welcome",
    drip2: "day2",
};

export default function useDripReferral() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [state, setState] = useState({
        kind: null,
        lovedOneName: null,
        dismissed: false,
    });

    useEffect(() => {
        const ref = (searchParams.get("ref") || "").toLowerCase();
        const kind = REF_TO_KIND[ref];
        // Dev-only breadcrumb — makes "is the hook even firing?" a
        // one-line answer from the browser console. Silent in production
        // builds where NODE_ENV !== "development".
        if (process.env.NODE_ENV === "development") {
            console.info("[useDripReferral] mount", { url: window.location.href, ref, kind });
        }
        if (!kind) return;

        let cancelled = false;
        (async () => {
            let lovedOneName = "your loved one";
            try {
                const r = await api.post("/me/drip-click", {
                    kind,
                    referrer: document.referrer || "",
                });
                if (cancelled) return;
                const n = (r?.data?.loved_one_name || "").trim();
                if (n) lovedOneName = n;
            } catch (_e) {
                // Endpoint down? The caregiver came from the email — still
                // show the banner, just with the generic fallback name.
            }
            try {
                track("drip_click", { kind, ref });
            } catch (_e) { /* analytics best-effort */ }

            if (cancelled) return;
            setState({ kind, lovedOneName, dismissed: false });

            // Strip ?ref from the URL so a refresh doesn't re-record
            // the click. Everything else in the querystring stays.
            const next = new URLSearchParams(searchParams);
            next.delete("ref");
            setSearchParams(next, { replace: true });
        })();

        return () => { cancelled = true; };
    }, []);

    const dismiss = () => setState(s => ({ ...s, dismissed: true }));

    return { ...state, dismiss };
}

export { REF_TO_KIND };
